import { CampaignV2Status } from '@/services/endpoints/campaigns/types';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, getDate, getDaysInMonth, isSameDay, isSameMonth, startOfMonth, startOfWeek } from 'date-fns';

export type CalendarCampaign = {
  id: string;
  name: string;
  providerType: string;
  status: CampaignV2Status;
  scheduledStart?: string;
  createdOn: string;
  updatedOn?: string;
  stats?: {
    totalSent?: number;
    totalDelivered?: number;
  };
};

export type CalendarDayCell = {
  date: Date;
  day: number;
  inMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  hasScheduled: boolean;
  hasSent: boolean;
};

const WEEK_STARTS_ON_MONDAY = { weekStartsOn: 1 as const };

export const shiftVisibleMonth = (month: Date, direction: 1 | -1) => startOfMonth(addMonths(month, direction));

export const selectedDayInMonth = (selected: Date, month: Date) => {
  const monthStart = startOfMonth(month);
  const day = Math.min(getDate(selected), getDaysInMonth(monthStart));

  return new Date(monthStart.getFullYear(), monthStart.getMonth(), day);
};

export const campaignCalendarDate = (campaign: Pick<CalendarCampaign, 'status' | 'scheduledStart' | 'createdOn' | 'updatedOn'>) => {
  if (campaign.scheduledStart) return new Date(campaign.scheduledStart);
  if ((campaign.status === 'ACTIVE' || campaign.status === 'COMPLETED') && campaign.updatedOn) return new Date(campaign.updatedOn);
  return new Date(campaign.createdOn);
};

const scheduledStartIsInFuture = (scheduledStart: string | undefined, now: Date) => {
  if (!scheduledStart) return false;
  const start = new Date(scheduledStart);
  return !Number.isNaN(start.getTime()) && start.getTime() > now.getTime();
};

export const campaignDotKind = (campaign: Pick<CalendarCampaign, 'status' | 'scheduledStart'>, now: Date): 'scheduled' | 'sent' | null => {
  if (campaign.status === 'SCHEDULED' || (campaign.status === 'ACTIVE' && scheduledStartIsInFuture(campaign.scheduledStart, now))) return 'scheduled';
  if (campaign.status === 'COMPLETED' || campaign.status === 'ACTIVE') return 'sent';
  return null;
};

export const campaignDeliveryRate = (campaign: CalendarCampaign, now: Date = new Date()) => {
  if (campaignDotKind(campaign, now) !== 'sent') return undefined;
  const sent = campaign.stats?.totalSent;
  const delivered = campaign.stats?.totalDelivered;
  if (!sent || delivered === undefined) return undefined;

  return `${Math.round((delivered / sent) * 100)}%`;
};

export const campaignsOnDay = (campaigns: CalendarCampaign[], day: Date) =>
  campaigns
    .filter((campaign) => campaign.status !== 'DELETED' && isSameDay(campaignCalendarDate(campaign), day))
    .sort((left, right) => campaignCalendarDate(left).getTime() - campaignCalendarDate(right).getTime());

export const buildMonthDays = (month: Date, selected: Date, today: Date, campaigns: CalendarCampaign[]): CalendarDayCell[] => {
  const monthStart = startOfMonth(month);
  const visibleDays = eachDayOfInterval({
    start: startOfWeek(monthStart, WEEK_STARTS_ON_MONDAY),
    end: endOfWeek(endOfMonth(month), WEEK_STARTS_ON_MONDAY),
  });

  return visibleDays.map((date) => {
    const inMonth = isSameMonth(date, monthStart);
    const dayCampaigns = inMonth ? campaignsOnDay(campaigns, date) : [];

    return {
      date,
      day: getDate(date),
      inMonth,
      isToday: inMonth && isSameDay(date, today),
      isSelected: inMonth && isSameDay(date, selected),
      hasScheduled: dayCampaigns.some((campaign) => campaignDotKind(campaign, today) === 'scheduled'),
      hasSent: dayCampaigns.some((campaign) => campaignDotKind(campaign, today) === 'sent'),
    };
  });
};
